'use client';

import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

// Recursively flattens nested react-hook-form error objects to extract dot-notated string paths
const getFlatErrorKeys = (obj: Record<string, unknown> | null | undefined, prefix = ''): string[] => {
  if (!obj || typeof obj !== 'object') return [];

  return Object.keys(obj).flatMap((key) => {
    const value = obj[key] as Record<string, unknown> | null | undefined;
    const newPrefix = prefix ? `${prefix}.${key}` : key;

    // If the field contains a react-hook-form error signature (like message or type)
    if (value && (value.message || value.type)) {
      return [newPrefix];
    }

    // Recurse into nested objects or array indices
    if (typeof value === 'object') {
      return getFlatErrorKeys(value as Record<string, unknown>, newPrefix);
    }

    return [];
  });
};

interface ScrollToErrorProps {
  offset?: number;
}

/**
 * Headless component designed to enhance the User Experience (UX) of React Hook Form forms.
 * When a user attempts to submit a form that has validation errors, this component
 * automatically scrolls the viewport to the first invalid field and applies focus to it.
 *
 * It calculates DOM positions (getBoundingClientRect) to scroll to the visually highest error first.
 */
const ScrollToError = ({ offset = 300 }: ScrollToErrorProps) => {
  const {
    formState: { errors, submitCount, isValidating },
  } = useFormContext();
  const lastSubmitCountRef = useRef(0);

  useEffect(() => {
    // Only trigger when submission count has increased and RHF validation is finished
    if (submitCount > lastSubmitCountRef.current && !isValidating) {
      lastSubmitCountRef.current = submitCount;

      const errorKeys = getFlatErrorKeys(errors as Record<string, unknown>);
      if (errorKeys.length === 0) return;

      const elements: HTMLElement[] = [];

      errorKeys.forEach((key) => {
        // Helper: check if element is actually visible (not a hidden input, display:none, etc.)
        const isVisible = (e: HTMLElement | null): boolean => {
          if (!e) return false;
          const rect = e.getBoundingClientRect();
          // Element is visible if it has dimensions or is in normal flow
          if (e.offsetParent !== null) return true;
          // Also accept fixed/sticky elements with height
          return rect.height > 0 || rect.width > 0;
        };

        // Try ID-based lookups first (most reliable for wrapper divs)
        let el = document.getElementById(key) as HTMLElement | null;
        if (!el || !isVisible(el)) el = document.getElementById(key.replace(/\./g, '-')) as HTMLElement | null;
        if (!el || !isVisible(el)) el = document.getElementById(key.replace(/\./g, '_')) as HTMLElement | null;

        // Fall back to name-based lookup but only accept visible elements
        if (!el || !isVisible(el)) {
          const byName = document.querySelector(`[name="${key}"]`) as HTMLElement | null;
          if (isVisible(byName)) el = byName;
        }

        // Partial attribute selectors as last resort
        if (!el || !isVisible(el)) {
          const byPartialId = document.querySelector(`[id*="${key}"]`) as HTMLElement | null;
          if (isVisible(byPartialId)) el = byPartialId;
        }
        if (!el || !isVisible(el)) {
          const byPartialName = document.querySelector(`[name*="${key}"]`) as HTMLElement | null;
          if (isVisible(byPartialName)) el = byPartialName;
        }

        if (el && isVisible(el)) {
          elements.push(el);
        }
      });

      if (elements.length === 0) return;

      // Sort elements by visual order (highest to lowest top positions relative to viewport)
      const sortedElements = elements.sort((a, b) => {
        const rectA = a.getBoundingClientRect();
        const rectB = b.getBoundingClientRect();
        return rectA.top + window.scrollY - (rectB.top + window.scrollY);
      });

      const firstInvalidField = sortedElements[0];

      // Calculate scroll coordinates with offset to clear sticky headers
      const elementTop = firstInvalidField.getBoundingClientRect().top + window.scrollY;
      const targetTop = elementTop - offset;

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
      });

      // Safely apply focus to element
      firstInvalidField.focus?.({ preventScroll: true });
    }
  }, [submitCount, isValidating, errors, offset]);

  return null;
};

export default ScrollToError;
